import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/public/SiteFooter";
import PageHero from "@/components/public/PageHero";
import { useSite } from "@/context/SiteContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search as SearchIcon, MapPin, Navigation, Store, ArrowRight, LayoutGrid, Map as MapIcon, Handshake, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import LaundryMap from "@/components/LaundryMap";
import { Stars } from "@/components/ReviewsSection";

export default function Search() {
  const [params] = useSearchParams();
  const { content } = useSite();
  const sc = content?.search || {};
  const [q, setQ] = useState(params.get("q") || "");
  const [coords, setCoords] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [coverage, setCoverage] = useState(null);

  const fetchResults = async (query = "", c = coords) => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (c) {
        params.lat = c.lat;
        params.lng = c.lng;
      }
      const { data } = await api.get("/laundries", { params });
      setResults(data);
    } catch {
      toast.error("Could not load laundries");
    } finally {
      setLoading(false);
    }
  };

  const fetchCoverage = async (c) => {
    try {
      const { data } = await api.get("/coverage", { params: { lat: c.lat, lng: c.lng } });
      setCoverage(data);
    } catch {
      setCoverage(null);
    }
  };

  useEffect(() => {
    fetchResults(params.get("q") || "");
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(c);
        fetchResults(q, c);
        fetchCoverage(c);
        toast.success("Sorted by distance from you");
      },
      () => toast.error("Could not get your location")
    );
  };

  return (
    <div className="public-site min-h-screen bg-slate-50">
      <Navbar dark />
      <PageHero overline={sc.overline || "Find a laundry"} title={sc.title || "Find a laundry"} subtitle={sc.subtitle}>
        <div className="mt-8 flex max-w-3xl flex-col gap-2 rounded-3xl bg-white p-2 shadow-2xl shadow-black/30 sm:flex-row sm:rounded-full">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-brand" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchResults(q)}
              placeholder={sc.placeholder || "e.g. London, Manchester, EC1A…"}
              className="h-12 w-full rounded-full bg-transparent pl-12 pr-4 text-base text-slate-900 outline-none placeholder:text-slate-400"
              data-testid="search-input"
            />
          </div>
          <Button onClick={useMyLocation} variant="ghost" className="h-12 rounded-full px-5 text-slate-600 hover:bg-slate-100" data-testid="use-location-btn">
            <Navigation className="mr-2 h-4 w-4" /> Use my location
          </Button>
          <Button onClick={() => fetchResults(q)} className="h-12 rounded-full bg-brand px-8 text-base font-bold hover:bg-brand-hover" data-testid="search-btn">
            Search
          </Button>
        </div>
      </PageHero>
      <div className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
        {coverage && !coverage.in_area && coverage.nearest_partner && (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="partner-coverage-banner">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                <Handshake className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-heading text-base font-semibold text-amber-900">You're outside our local coverage — but we've got you covered</p>
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-600 px-2.5 py-0.5 text-xs font-semibold text-white" data-testid="partner-network-badge">
                    <Handshake className="h-3 w-3" /> Powered by our Partner Network
                  </span>
                </div>
                <p className="mt-1 text-sm text-amber-800">
                  No laundry serves your exact area yet, so your order will be handled by our partner{" "}
                  <span className="font-semibold">{coverage.nearest_partner.name}</span> ({coverage.nearest_partner.city}, {coverage.nearest_partner.distance_km} km away).
                </p>
                <p className="mt-1 text-xs text-amber-700" data-testid="partner-pricing-note">
                  Estimated pricing: standard item rates + £4.99 pickup &amp; delivery. Your exact total is confirmed at checkout before you pay.
                </p>
                <Link to={`/laundry/${coverage.nearest_partner.id}`} data-testid="partner-book-btn"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700">
                  Book with our partner <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        )}
        {coverage && coverage.in_area && (
          <div className="mt-6 flex items-center gap-2 rounded-2xl border border-green-200 bg-green-50 px-5 py-3 text-sm font-medium text-green-800" data-testid="coverage-in-area">
            <CheckCircle2 className="h-4 w-4" /> Great news — we serve your area. Pick a laundry below.
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-slate-500" data-testid="results-count">
            {loading ? "Searching…" : `${results.length} laundr${results.length === 1 ? "y" : "ies"} found`}
          </p>
          <div className="flex gap-1 rounded-full border border-slate-200 bg-white p-1">
            <button onClick={() => setView("list")} data-testid="view-list-btn"
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${view === "list" ? "bg-brand text-white" : "text-slate-500 hover:text-slate-900"}`}>
              <LayoutGrid className="h-4 w-4" /> List
            </button>
            <button onClick={() => setView("map")} data-testid="view-map-btn"
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${view === "map" ? "bg-brand text-white" : "text-slate-500 hover:text-slate-900"}`}>
              <MapIcon className="h-4 w-4" /> Map
            </button>
          </div>
        </div>

        {view === "map" && !loading && (
          <div className="mt-4">
            <LaundryMap laundries={results} userCoords={coords} />
          </div>
        )}

        {view === "list" && (
        <div className="mt-4 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-52 animate-pulse rounded-3xl border border-slate-200 bg-white" />
            ))}

          {!loading && results.length === 0 && (
            <div className="col-span-full rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500" data-testid="no-results">
              <Store className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3">No laundries found. Try a different search.</p>
            </div>
          )}

          {!loading &&
            results.map((l) => (
              <Link
                key={l.id}
                to={`/laundry/${l.id}`}
                data-testid={`laundry-card-${l.id}`}
                className="group flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-brand/30 hover:shadow-xl hover:shadow-brand/5"
              >
                {l.cover_image && (
                  <div className="-mx-7 -mt-7 mb-5 h-40 overflow-hidden">
                    <img src={l.cover_image} alt={l.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid={`laundry-cover-${l.id}`} />
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-brand transition-transform group-hover:-rotate-6">
                    <Store className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  {l.distance_km != null && (
                    <Badge variant="secondary" className="font-mono text-xs">
                      {l.distance_km} km
                    </Badge>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {l.is_partner && (
                    <Badge className="gap-1 bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`partner-badge-${l.id}`}>
                      <Handshake className="h-3 w-3" /> Partner
                    </Badge>
                  )}
                  {l.in_service_area === true && (
                    <Badge variant="outline" className="gap-1 border-green-300 text-green-700">
                      <CheckCircle2 className="h-3 w-3" /> In your area
                    </Badge>
                  )}
                  {l.in_service_area === false && !l.is_partner && (
                    <Badge variant="outline" className="border-slate-300 text-slate-400">Outside area</Badge>
                  )}
                </div>
                <h3 className="mt-4 font-heading text-xl font-bold text-slate-900">{l.name}</h3>
                <div className="mt-1.5 flex items-center gap-2 text-sm" data-testid={`laundry-card-rating-${l.id}`}>
                  {l.rating?.count > 0 ? (
                    <><Stars value={l.rating.avg} size="h-3.5 w-3.5" /><span className="font-bold text-slate-900">{l.rating.avg}</span><span className="text-slate-400">({l.rating.count})</span></>
                  ) : <span className="rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-brand">New on Just Laundry</span>}
                </div>
                <p className="mt-1 line-clamp-2 text-sm text-slate-500">{l.description || "Quality laundry service."}</p>
                <p className="mt-3 flex items-center gap-1.5 text-sm text-slate-600">
                  <MapPin className="h-4 w-4 text-slate-400" /> {l.city}
                  {l.postcode ? `, ${l.postcode}` : ""}
                </p>
                {l.rating?.latest && (
                  <blockquote className="mt-4 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid={`laundry-card-review-${l.id}`}>
                    <p className="line-clamp-2 italic">“{l.rating.latest.comment}”</p>
                    <footer className="mt-1.5 flex items-center gap-2 text-xs font-semibold not-italic text-slate-500">{l.rating.latest.customer_name} <Stars value={l.rating.latest.rating} size="h-3 w-3" /></footer>
                  </blockquote>
                )}
                <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
                  <span className="text-xs font-medium text-slate-400">{l.item_count} item prices</span>
                  <span className="flex items-center gap-1 rounded-full bg-brand px-4 py-1.5 text-sm font-bold text-white transition-colors group-hover:bg-brand-hover">
                    View &amp; book <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            ))}
        </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
